import React, { useState, useEffect } from 'react';
import {
  Layout, Typography, Card, Table, Tag, Space, Button,
  DatePicker, Select, Spin, notification, Divider, Input
} from 'antd';
import {
  FilePdfOutlined, FilterOutlined, ClearOutlined,
  DollarCircleOutlined, SearchOutlined, DownloadOutlined, PrinterOutlined
} from '@ant-design/icons';
import {
  PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image
} from '@react-pdf/renderer';
import axiosClient from '../../api/axiosClient';
import {
  APPBACKGROUND_COLOR,
  COMPANY_NAME, COMPANY_ADDRESS_LINE1, COMPANY_ADDRESS_LINE2,
  COMPANY_PHONE, COMPANY_LOGO_PATH, REPORT_FOOTER_TEXT,
  SYSTEMNAME, MAINVERSION
} from '../appsettings';
import logo from '../../sitelogo.png';

const { Content } = Layout;
const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// ─── Shared helpers ─────────────────────────────────────────────────────────

const fmt = (n) =>
  Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');

// ─── PDF Styles  (mirrors AdminFrontend/src/pages/invoices/invoice-pdf.js) ──

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 30,
    fontFamily: 'Helvetica',
    fontSize: 10,
  },
  header: {
    marginBottom: 20,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerLeft: { flexDirection: 'column' },
  headerRight: { alignItems: 'flex-end' },
  logo: { width: 100, marginBottom: 10 },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#333',
  },
  subtitle: { fontSize: 10, color: '#666', marginTop: 5 },
  billTo: {
    marginTop: 20,
    marginBottom: 20,
    padding: 10,
    backgroundColor: '#f9f9f9',
    borderRadius: 4,
  },
  billToTitle: {
    fontWeight: 'bold',
    marginBottom: 5,
    fontSize: 11,
    color: '#333',
  },
  table: { marginTop: 10, width: '100%' },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingBottom: 5,
    marginBottom: 5,
    fontWeight: 'bold',
    backgroundColor: '#f0f0f0',
    padding: 8,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingHorizontal: 8,
  },
  colRef:    { width: '30%' },
  colType:   { width: '25%' },
  colDate:   { width: '25%' },
  colAmount: { width: '20%', textAlign: 'right' },
  totalsSection: { marginTop: 20, alignItems: 'flex-end' },
  totalRow: {
    flexDirection: 'row',
    marginBottom: 3,
    width: 200,
    justifyContent: 'space-between',
  },
  totalLabel: { fontWeight: 'bold', color: '#666' },
  totalValue: { color: '#333' },
  grandTotal: {
    borderTopWidth: 1,
    borderTopColor: '#333',
    paddingTop: 5,
    marginTop: 5,
    fontWeight: 'bold',
    fontSize: 14,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#eee',
    color: '#888',
    fontSize: 8,
  },
});

// ─── Payment Receipt PDF (New) ────────────────────────────────────────────────

const PaymentReceiptPDF = ({ invoice, payment }) => (
  <Document title={`Receipt-${payment.payment_ref || payment.inv_pay_id}`}>
    <Page size="A6" orientation="landscape" style={[styles.page, { padding: 20 }]}>
      <View style={[styles.header, { marginBottom: 10 }]}>
        <View style={styles.headerLeft}>
          <Image src={logo} style={{ width: 60, marginBottom: 5 }} />
          <Text style={{ fontSize: 9, fontWeight: 'bold' }}>{COMPANY_NAME}</Text>
        </View>
        <View style={styles.headerRight}>
          <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#209c96' }}>RECEIPT</Text>
          <Text style={{ fontSize: 8 }}>No: {payment.payment_ref || payment.inv_pay_id}</Text>
          <Text style={{ fontSize: 8 }}>Date: {fmtDate(payment.process_dt)}</Text>
        </View>
      </View>

      <View style={{ marginBottom: 10 }}>
        <Text style={{ fontSize: 8, color: '#666' }}>Received From:</Text>
        <Text style={{ fontSize: 10, fontWeight: 'bold' }}>{invoice.member_name || localStorage.getItem('DISPLAYNAME') || 'Member'}</Text>
        <Text style={{ fontSize: 8 }}>Member ID: {invoice.member_id || 'N/A'}</Text>
      </View>

      <View style={{ backgroundColor: '#f9f9f9', padding: 8, borderRadius: 4, marginBottom: 10 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
          <Text style={{ fontSize: 8, color: '#666' }}>Payment For:</Text>
          <Text style={{ fontSize: 8, fontWeight: 'bold' }}>Invoice #{invoice.inv_ref}</Text>
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
          <Text style={{ fontSize: 8, color: '#666' }}>Payment Method:</Text>
          <Text style={{ fontSize: 8 }}>{payment.payment_type}</Text>
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', borderTopWidth: 1, borderTopColor: '#ddd', paddingTop: 4 }}>
          <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Amount Paid:</Text>
          <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#209c96' }}>{invoice.invoice_currency} {fmt(payment.paid_amount)}</Text>
        </View>
      </View>

      <View style={{ marginTop: 'auto', textAlign: 'center' }}>
        <Text style={{ fontSize: 7, color: '#aaa' }}>{REPORT_FOOTER_TEXT} • Thank you for your payment!</Text>
      </View>
    </Page>
  </Document>
);

// ─── Per-Invoice PDF  (mirrors AdminFrontend invoice-pdf.js) ─────────────────

const SingleInvoicePDF = ({ invoice }) => {
  if (!invoice) return <Document><Page size="A4"><Text>No Data</Text></Page></Document>;

  const balance = Math.max(
    0,
    parseFloat(invoice.tobe_paid_amount ?? invoice.invoice_total) - parseFloat(invoice.total_paid_amount)
  );
  const cur = invoice.invoice_currency;

  const statusColor =
    invoice.inv_status === 'Paid' || invoice.inv_status === 'Completed'
      ? 'green'
      : invoice.inv_status === 'Cancelled'
      ? 'red'
      : 'orange';

  return (
    <Document title={`Invoice-${invoice.inv_ref}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Image src={logo} style={styles.logo} />
            <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{COMPANY_NAME}</Text>
            <Text>{COMPANY_ADDRESS_LINE1}</Text>
            <Text>{COMPANY_ADDRESS_LINE2}</Text>
            <Text>Contact: {COMPANY_PHONE}</Text>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.title}>INVOICE</Text>
            <Text style={styles.subtitle}>#{invoice.inv_ref}</Text>
            <Text style={styles.subtitle}>Date: {new Date(invoice.process_dt).toLocaleString()}</Text>
            <Text style={{
              marginTop: 10,
              color: statusColor,
              fontWeight: 'bold',
              borderWidth: 1,
              borderColor: statusColor,
              padding: '2 8',
              borderRadius: 4,
            }}>{invoice.inv_status.toUpperCase()}</Text>
          </View>
        </View>

        <View style={styles.billTo}>
          <Text style={styles.billToTitle}>BILL TO:</Text>
          <Text style={{ fontWeight: 'bold', fontSize: 12 }}>
            {invoice.member_name || localStorage.getItem('DISPLAYNAME') || 'Member'}
          </Text>
          <Text>ID: {invoice.member_id || 'N/A'}</Text>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={styles.colRef}>Ref Number</Text>
            <Text style={styles.colType}>Type</Text>
            <Text style={styles.colDate}>Date</Text>
            <Text style={styles.colAmount}>Amount ({cur})</Text>
          </View>

          {invoice.payments && invoice.payments.length > 0 ? invoice.payments.map((pay, idx) => (
            <View key={idx} style={styles.tableRow}>
              <Text style={styles.colRef}>{pay.payment_ref || 'N/A'}</Text>
              <Text style={styles.colType}>{pay.payment_type}</Text>
              <Text style={styles.colDate}>{fmtDate(pay.process_dt)}</Text>
              <Text style={styles.colAmount}>{fmt(pay.paid_amount)}</Text>
            </View>
          )) : (
            <View style={styles.tableRow}><Text style={{ color: '#888' }}>No payment records yet.</Text></View>
          )}
        </View>

        <View style={styles.totalsSection}>
          {parseFloat(invoice.total_discount) > 0 && (
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Discount:</Text>
              <Text style={styles.totalValue}>- {fmt(invoice.total_discount)}</Text>
            </View>
          )}
          {parseFloat(invoice.total_tax) > 0 && (
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Tax:</Text>
              <Text style={styles.totalValue}>+ {fmt(invoice.total_tax)}</Text>
            </View>
          )}
          <View style={[styles.totalRow, styles.grandTotal]}>
            <Text style={styles.totalLabel}>Grand Total:</Text>
            <Text style={{ fontSize: 14, fontWeight: 'bold' }}>{cur} {fmt(invoice.invoice_total)}</Text>
          </View>

          <View style={{ height: 10 }} />

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Paid Amount:</Text>
            <Text style={styles.totalValue}>{fmt(invoice.total_paid_amount)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Balance Due:</Text>
            <Text style={{ ...styles.totalValue, color: balance > 0 ? 'red' : 'green' }}>{fmt(balance)}</Text>
          </View>
        </View>

        <View style={styles.footer}>
          <Text>Note: {invoice.invoice_note || 'Thank you for choosing ' + COMPANY_NAME + '!'}</Text>
          <Text style={{ marginTop: 5 }}>{REPORT_FOOTER_TEXT} • {SYSTEMNAME} v{MAINVERSION}</Text>
        </View>
      </Page>
    </Document>
  );
};

// ─── Bulk Invoice List PDF ───────────────────────────────────────────────────

const InvoiceListPDF = ({ invoices, dateRange }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Image src={logo} style={styles.logo} />
          <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{COMPANY_NAME}</Text>
          <Text>{COMPANY_ADDRESS_LINE1}</Text>
          <Text>{COMPANY_ADDRESS_LINE2}</Text>
          <Text>Contact: {COMPANY_PHONE}</Text>
        </View>
        <View style={styles.headerRight}>
          <Text style={styles.title}>INVOICE HISTORY</Text>
          <Text style={styles.subtitle}>
            {dateRange && dateRange.length === 2 ? `Period: ${dateRange[0]} to ${dateRange[1]}` : 'All Invoices'}
          </Text>
        </View>
      </View>

      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={styles.colRef}>Ref Number</Text>
          <Text style={styles.colType}>Date</Text>
          <Text style={styles.colDate}>Status</Text>
          <Text style={{ width: '15%', textAlign: 'right' }}>Total</Text>
          <Text style={{ width: '15%', textAlign: 'right' }}>Paid</Text>
          <Text style={{ width: '15%', textAlign: 'right' }}>Balance</Text>
        </View>
        {invoices.map((inv, i) => {
          const bal = Math.max(0, parseFloat(inv.invoice_total) - parseFloat(inv.total_paid_amount));
          return (
            <View key={i} style={[styles.tableRow, i % 2 === 1 ? { backgroundColor: '#f9f9f9' } : {}]}>
              <Text style={styles.colRef}>{inv.inv_ref}</Text>
              <Text style={styles.colType}>{fmtDate(inv.process_dt)}</Text>
              <Text style={styles.colDate}>{inv.inv_status}</Text>
              <Text style={{ width: '15%', textAlign: 'right' }}>{fmt(inv.invoice_total)}</Text>
              <Text style={{ width: '15%', textAlign: 'right' }}>{fmt(inv.total_paid_amount)}</Text>
              <Text style={{ width: '15%', textAlign: 'right', color: bal > 0 ? 'red' : 'green' }}>{fmt(bal)}</Text>
            </View>
          );
        })}
      </View>

      <View style={styles.footer}>
        <Text>{REPORT_FOOTER_TEXT} • {SYSTEMNAME} v{MAINVERSION}</Text>
      </View>
    </Page>
  </Document>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const MemberInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState(null);
  const [dateRangeFilter, setDateRangeFilter] = useState([]);
  const [searchRef, setSearchRef] = useState('');

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get('/portal/get_invoices.php');
      if (res.data.status === 'success') {
        setInvoices(res.data.data);
        setFilteredInvoices(res.data.data);
      } else {
        notification.error({ message: 'Failed to load invoices', description: res.data.message });
      }
    } catch (e) {
      notification.error({ message: 'Network Error', description: 'Could not fetch invoicing data.' });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let f = [...invoices];
    if (statusFilter && statusFilter !== 'All')
      f = f.filter(i => i.inv_status.toUpperCase() === statusFilter.toUpperCase());
    if (dateRangeFilter && dateRangeFilter.length === 2) {
      const s = dateRangeFilter[0].startOf('day').valueOf();
      const e = dateRangeFilter[1].endOf('day').valueOf();
      f = f.filter(i => { const d = new Date(i.process_dt).getTime(); return d >= s && d <= e; });
    }
    if (searchRef) f = f.filter(i => i.inv_ref.toLowerCase().includes(searchRef.toLowerCase()));
    setFilteredInvoices(f);
    notification.success({ message: 'Filters Applied', description: `Showing ${f.length} record(s).`, placement: 'bottomRight' });
  };

  const clearFilters = () => {
    setStatusFilter(null);
    setDateRangeFilter([]);
    setSearchRef('');
    setFilteredInvoices(invoices);
  };

  const getStatusTag = (status) => {
    const colors = { Paid: 'success', Completed: 'success', Partial: 'processing', Pending: 'warning', Unpaid: 'error', Cancelled: 'default' };
    return <Tag color={colors[status] || 'default'} style={{ borderRadius: 6, fontWeight: 500 }}>{status.toUpperCase()}</Tag>;
  };

  const columns = [
    {
      title: 'Ref Number', dataIndex: 'inv_ref', key: 'inv_ref',
      render: t => <Typography.Text strong style={{ color: '#209c96' }}>{t}</Typography.Text>,
    },
    {
      title: 'Date', dataIndex: 'process_dt', key: 'process_dt',
      render: d => fmtDate(d),
      sorter: (a, b) => new Date(a.process_dt) - new Date(b.process_dt),
      responsive: ['md'],
    },
    {
      title: 'Total', dataIndex: 'invoice_total', key: 'invoice_total',
      render: (v, r) => `${r.invoice_currency} ${fmt(v)}`,
      responsive: ['sm'],
    },
    {
      title: 'Paid', dataIndex: 'total_paid_amount', key: 'total_paid_amount',
      render: (v, r) => <Typography.Text type="success">{r.invoice_currency} {fmt(v)}</Typography.Text>,
      responsive: ['lg'],
    },
    {
      title: 'Balance', key: 'balance',
      render: (_, r) => {
        const bal = Math.max(0, parseFloat(r.invoice_total) - parseFloat(r.total_paid_amount));
        return <Typography.Text type={bal > 0 ? 'danger' : 'secondary'}>{r.invoice_currency} {fmt(bal)}</Typography.Text>;
      },
    },
    { title: 'Status', dataIndex: 'inv_status', key: 'inv_status', render: s => getStatusTag(s) },
    {
      title: 'Invoice PDF', key: 'pdf',
      render: (_, record) => (
        <PDFDownloadLink
          document={<SingleInvoicePDF invoice={record} />}
          fileName={`Invoice_${record.inv_ref}.pdf`}
        >
          {({ loading: pl }) => (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={pl}
              style={{ borderRadius: 6, background: '#209c96', borderColor: '#209c96', color: '#fff', fontSize: 12 }}
            >
              PDF
            </Button>
          )}
        </PDFDownloadLink>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Title level={2} style={{ color: '#1a1a1a', fontWeight: 800, marginBottom: 8, fontFamily: "'Inter', sans-serif" }}>
          Billing &amp; Invoices 💳
        </Title>
        <p style={{ color: '#666', fontSize: 16, marginBottom: 32 }}>
          Review your transactional history, outstanding balances, and export official billing statements.
        </p>

        <Card bordered={false} style={{ borderRadius: 20, boxShadow: '0 8px 32px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
            <Space wrap style={{ flexGrow: 1 }}>
              <Input
                placeholder="Search Ref #"
                prefix={<SearchOutlined />}
                value={searchRef}
                onChange={e => setSearchRef(e.target.value)}
                style={{ width: 150, borderRadius: 8 }}
              />
              <Select
                placeholder="Filter by Status"
                style={{ width: 165 }}
                value={statusFilter}
                onChange={v => setStatusFilter(v)}
                allowClear
              >
                <Option value="All">All Statuses</Option>
                <Option value="Paid">Paid</Option>
                <Option value="Partial">Partial</Option>
                <Option value="Pending">Pending</Option>
                <Option value="Unpaid">Unpaid</Option>
                <Option value="Cancelled">Cancelled</Option>
              </Select>
              <RangePicker
                style={{ borderRadius: 8 }}
                value={dateRangeFilter}
                onChange={dates => setDateRangeFilter(dates)}
              />
              <Button type="primary" icon={<FilterOutlined />} onClick={applyFilters} style={{ borderRadius: 8 }}>
                Filter
              </Button>
              <Button onClick={clearFilters} icon={<ClearOutlined />} style={{ borderRadius: 8 }}>
                Clear
              </Button>
            </Space>

            <PDFDownloadLink
              document={<InvoiceListPDF invoices={filteredInvoices} dateRange={dateRangeFilter && dateRangeFilter.length === 2 ? [dateRangeFilter[0].format('YYYY-MM-DD'), dateRangeFilter[1].format('YYYY-MM-DD')] : []} />}
              fileName={`Invoice_History_${new Date().toISOString().split('T')[0]}.pdf`}
            >
              {({ loading: pl }) => (
                <Button
                  type="primary"
                  icon={<FilePdfOutlined />}
                  loading={pl || loading}
                  style={{ borderRadius: 8, background: '#f5222d', borderColor: '#f5222d', boxShadow: '0 4px 14px rgba(245,34,45,0.35)' }}
                >
                  {pl ? 'Preparing...' : 'Export All (PDF)'}
                </Button>
              )}
            </PDFDownloadLink>
          </div>

          <Divider style={{ margin: '16px 0' }} />

          <Table
            dataSource={filteredInvoices}
            columns={columns}
            rowKey="inv_id"
            loading={loading}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            scroll={{ x: 900 }}
            expandable={{
              expandedRowRender: record => (
                <div style={{ padding: 16, background: '#fafafa', borderRadius: 8, border: '1px dashed #e8e8e8' }}>
                  <Typography.Title level={5}><DollarCircleOutlined /> Payment Breakdown</Typography.Title>
                  {record.payments?.length > 0 ? (
                    <Table
                      dataSource={record.payments}
                      columns={[
                        { title: 'Payment Ref', dataIndex: 'payment_ref', render: v => v || 'N/A' },
                        { title: 'Type', dataIndex: 'payment_type' },
                        { title: 'Amount', dataIndex: 'paid_amount', render: v => `${record.invoice_currency} ${fmt(v)}` },
                        { title: 'Date', dataIndex: 'process_dt', render: d => new Date(d).toLocaleString() },
                        {
                          title: 'Receipt',
                          key: 'receipt',
                          align: 'right',
                          render: (_, pay) => (
                            <PDFDownloadLink
                              document={<PaymentReceiptPDF invoice={record} payment={pay} />}
                              fileName={`Receipt_${pay.payment_ref || pay.inv_pay_id}.pdf`}
                            >
                              {({ loading: pLoading }) => (
                                <Button 
                                  size="small" 
                                  type="dashed" 
                                  icon={<PrinterOutlined />} 
                                  loading={pLoading}
                                  style={{ fontSize: 11, borderRadius: 4 }}
                                >
                                  Receipt
                                </Button>
                              )}
                            </PDFDownloadLink>
                          )
                        }
                      ]}
                      pagination={false}
                      size="small"
                      rowKey="inv_pay_id"
                    />
                  ) : (
                    <Typography.Text type="secondary">No payment records for this invoice.</Typography.Text>
                  )}
                </div>
              ),
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default MemberInvoices;
